import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import type { TicketQuery } from '../api/endpoints';
import { ContractCockpitSection } from '../documents/ContractCockpitSection';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { TicketList } from '../ops/TicketCards';
import { TicketFilters } from './TicketsPage';

type Tab = 'open' | 'urgent' | 'handback' | 'overdue' | 'field' | 'unassigned';

const tabs: { key: Tab; label: string; q: TicketQuery }[] = [
  { key: 'open', label: 'Alle offenen', q: { status: ['open'], assigned: 'all', sort: 'priority' } },
  { key: 'urgent', label: 'Dringend', q: { status: ['open'], priority: ['URGENT'], assigned: 'all' } },
  { key: 'handback', label: 'Terminrückgaben', q: { status: ['open'], category: 'APPOINTMENT_HANDBACK', assigned: 'all', sort: 'priority' } },
  { key: 'overdue', label: 'Überfällig', q: { status: ['open'], due: 'overdue', assigned: 'all', sort: 'priority' } },
  { key: 'field', label: 'Rückfragen Außendienst', q: { status: ['open'], category: 'FIELD_SALES_REQUEST', assigned: 'all', sort: 'priority' } },
  { key: 'unassigned', label: 'Nicht zugewiesen', q: { status: ['open'], assigned: 'unassigned', sort: 'priority' } },
];

function Count({ value, label, to, tone }: { value?: number; label: string; to?: string; tone?: 'warn' | 'error' }) {
  const v = value ?? 0;
  const body = (
    <>
      <span className={tone && v > 0 ? `stat-value stat-${tone}` : 'stat-value'}>{v}</span>
      <span className="stat-label">{label}</span>
    </>
  );
  return <li className="stat">{to ? <Link to={to}>{body}</Link> : body}</li>;
}

// SupportPage: zentrale Arbeitsliste des Supports – offene und dringende Vorgänge, Rückfragen vom
// Außendienst, fällige Aufgaben und alle Klärungsfälle (Geo, KI, Outlook) eines Projekts.
// Eingebettet im Posteingang: nur die Rückfragen-Listen, gefiltert nach Gebiet/Makler.
export function SupportPage({
  embedded = false,
  focused = false,
  areaId,
  fieldSalesUserId,
}: {
  embedded?: boolean;
  focused?: boolean;
  areaId?: string;
  fieldSalesUserId?: string;
}) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const dash = useQuery({ queryKey: projectKeys.dashboard(project.id), queryFn: ({ signal }) => papi.dashboard(signal), refetchInterval: 60_000 });
  const [tab, setTab] = useState<Tab>('open');
  const [filter, setFilter] = useState<TicketQuery>(tabs[0]!.q);
  const s = dash.data?.support;
  const base = `/p/${project.key}`;
  const current = tabs.find((t) => t.key === tab) ?? tabs[0]!;
  const q = { ...(tab === 'open' ? filter : current.q), area_id: areaId, field_sales_user_id: fieldSalesUserId };
  return (
    <div className={embedded ? 'embedded-page support-page' : 'page support-page'}>
      {!embedded && (
        <header className="page-header">
          <h1>Support</h1>
          <Link className="btn" to={`${base}/tickets`}>
            Meine Rückfragen
          </Link>
        </header>
      )}
      {!embedded && (
      <section className="card" aria-label="Überblick">
        <ul className="stats">
          <Count value={s?.open_tickets} label="offene Rückfragen" />
          <Count value={s?.urgent_tickets} label="dringend" tone="error" />
          <Count value={s?.appointment_handbacks} label="Terminrückgaben" tone="warn" />
          <Count value={s?.overdue_tickets} label="überfällig" tone="warn" />
          <Count value={s?.field_sales_requests} label="vom Außendienst" />
          <Count value={s?.unassigned_tickets} label="nicht zugewiesen" tone="warn" />
          <Count value={s?.overdue_follow_ups} label="fällige Wiedervorlagen" tone="warn" to={`${base}/aufgaben?assigned=all&due=overdue`} />
          <Count value={s?.geo_review_open} label="Geo-Klärungen" to={can('lead.geo_review') ? `${base}/geo/klaerung` : undefined} />
          <Count value={s?.ai_reviews_pending} label="KI-Prüfungen" to={can('ai.review') ? `${base}/ki-pruefung` : undefined} />
          <Count value={s?.sync_failures} label="Outlook-Fehler" tone="error" to={can('integration.outlook.manage') ? `${base}/verwaltung?tab=integrationen` : undefined} />
          <Count value={s?.unassigned_appointments} label="Termine ohne Außendienst" tone="warn" to={`${base}/kalender`} />
        </ul>
      </section>
      )}
      {!focused && (
        <div className="tabs" role="tablist" aria-label="Arbeitslisten">
          {tabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'tab active' : 'tab'} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      <section className="card" role="tabpanel" aria-label={current.label}>
        {tab === 'open' && <TicketFilters value={filter} onChange={setFilter} manage />}
        <TicketList q={q} groupBy="work" />
      </section>
      {!embedded && !focused && <ContractCockpitSection />}
    </div>
  );
}
